import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Sliders, Search, Plus, Hash, RefreshCcw, X, Map as MapIcon } from 'lucide-react';
import { api } from '@/lib/api';
import { ws } from '@/lib/ws';
import { useFeed } from '@/contexts/FeedContext';
import { FEED_KINDS } from '@/lib/feed-config';
import FeedCard from '@/components/FeedCard';
import SponsoredCard from '@/components/SponsoredCard';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

export default function FeedPage() {
  const { kind, config } = useFeed();
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [tags, setTags] = useState([]);
  const [filterOverlay, setFilterOverlay] = useState('');
  const [coords, setCoords] = useState(null);
  const [ads, setAds] = useState([]);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (q) params.q = q;
      if (tags.length) params.hashtags = tags.join(',');
      if (filterOverlay) params.filter = filterOverlay;
      if (kind === 'marketplace' && coords) { params.lat = coords.lat; params.lng = coords.lng; }
      const { data } = await api.get(`/feeds/${kind}`, { params });
      setItems(data.items || []);
    } catch (e) { toast.error('Could not load feed'); }
    finally { setLoading(false); }
  }, [kind, q, tags, filterOverlay, coords]);

  // Fetch in-feed sponsored ads matching the current feed kind + active hashtag filters.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = { feed_kind: kind, limit: 3 };
        if (tags.length) params.hashtags = tags.join(',');
        const { data } = await api.get('/ads/serve', { params });
        if (!cancelled) setAds(Array.isArray(data) ? data.filter((a) => a.format === 'feed_post') : []);
      } catch {
        if (!cancelled) setAds([]);
      }
    })();
    return () => { cancelled = true; };
  }, [kind, tags]);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  // Live feed broadcast: prepend new posts that match current feed kind in real time.
  useEffect(() => {
    const off = ws.on((msg) => {
      if (!msg || msg.event !== 'live:post_created') return;
      const post = msg.post;
      if (!post) return;
      const targets = msg.feed_targets || post.feed_targets || [];
      if (!targets.includes(kind)) return;
      // If hashtag filter active, only prepend posts that include any selected tag
      if (tags.length > 0) {
        const hl = (post.hashtags || []).map((h) => h.toLowerCase());
        const match = tags.some((t) => hl.includes(t.toLowerCase()));
        if (!match) return;
      }
      setItems((cur) => {
        if (cur.some((p) => p.id === post.id)) return cur;
        return [post, ...cur].slice(0, 200);
      });
    });
    return () => { off && off(); };
  }, [kind, tags]);

  useEffect(() => {
    if (kind === 'marketplace' && !coords && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => setCoords({ lat: 40.73, lng: -73.99 }),
        { timeout: 4000 },
      );
    }
  }, [kind, coords]);

  // Pre-fill prefs once on mount per kind change
  useEffect(() => {
    if (user && Array.isArray(user.hashtag_prefs) && tags.length === 0) {
      // do not auto-apply; show as suggestions in drawer
    }
  }, [user, tags.length, kind]);

  const onTagClick = (h) => setTags((arr) => Array.from(new Set([...arr, h.toLowerCase()])));
  const removeTag = (h) => setTags((arr) => arr.filter((x) => x !== h));

  return (
    <div className="haze noise-overlay">
      <div className="mx-auto max-w-[720px] px-4 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight" data-testid="feed-title">{config.label} Feed</h1>
            <p className="mt-1 text-sm text-muted-foreground">{config.blurb}</p>
          </div>
          <div className="flex gap-2">
            <Drawer>
              <DrawerTrigger asChild>
                <Button variant="outline" size="sm" data-testid="feed-drawer-open-button"><Sliders className="mr-1 h-4 w-4" /> Feed</Button>
              </DrawerTrigger>
              <DrawerContent className="bg-[hsl(var(--app-bg-2))]">
                <DrawerHeader><DrawerTitle>Feed Settings</DrawerTitle></DrawerHeader>
                <div className="space-y-4 px-4 pb-6">
                  <div>
                    <div className="mb-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Pinned hashtags</div>
                    <div className="flex flex-wrap gap-2" data-testid="feed-drawer-pinned-hashtags">
                      {(user?.hashtag_prefs || []).map((h) => (
                        <button key={h} onClick={() => onTagClick(h)} className="rounded-full border border-[hsl(var(--app-border))] px-2.5 py-1 font-mono text-xs hover:feed-accent-border">#{h}</button>
                      ))}
                      {(user?.hashtag_prefs || []).length === 0 && <span className="text-xs text-muted-foreground">Add some in Settings.</span>}
                    </div>
                  </div>
                  <div>
                    <div className="mb-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Filter overlay</div>
                    <div className="grid grid-cols-2 gap-2" data-testid="feed-drawer-filters">
                      {[['', 'Chronological'], ['popular', 'Popular'], ['low_popularity', 'Low pop'], ['comment_count', 'Comments']].map(([v, l]) => (
                        <Button key={v} variant={filterOverlay === v ? 'default' : 'outline'} size="sm" onClick={() => setFilterOverlay(v)} data-testid={`feed-drawer-filter-${v || 'none'}`}>{l}</Button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="mb-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Communities</div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => nav('/communities')} data-testid="feed-drawer-communities">Browse</Button>
                      <Button size="sm" onClick={() => nav('/post/new')} data-testid="feed-drawer-create"><Plus className="mr-1 h-4 w-4" /> Create post</Button>
                    </div>
                  </div>
                </div>
              </DrawerContent>
            </Drawer>
            <Button size="sm" onClick={() => nav('/post/new')} data-testid="feed-create-post-button"><Plus className="mr-1 h-4 w-4" /> Post</Button>
            {kind === 'marketplace' && (
              <Button variant="outline" size="sm" onClick={() => nav('/feed/marketplace/map')} data-testid="feed-marketplace-map-toggle">
                <MapIcon className="mr-1 h-4 w-4" /> Map
              </Button>
            )}
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2" data-testid="feed-search-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder={`Search ${config.label}…`} value={q} onChange={(e) => setQ(e.target.value)} data-testid="feed-hashtag-search-input" />
          </div>
          <Button variant="outline" size="sm" onClick={fetchFeed} data-testid="feed-refresh-button"><RefreshCcw className="h-4 w-4" /></Button>
        </div>
        {tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {tags.map((h) => (
              <button key={h} onClick={() => removeTag(h)} className="feed-accent-border feed-accent-soft inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-mono text-xs">
                #{h} <X className="h-3 w-3" />
              </button>
            ))}
          </div>
        )}
        {/* Feed switcher — works on mobile (where sidebar is hidden) AND desktop */}
        <div className="-mx-4 mt-4 flex gap-2 overflow-x-auto px-4 pb-1 scrollbar-none" role="tablist" aria-label="Switch feed" data-testid="feed-switcher">
          {FEED_KINDS.map((f) => {
            const active = f.kind === kind;
            const path = f.kind === 'main' ? '/feed' : `/feed/${f.kind}`;
            return (
              <button
                key={f.kind}
                role="tab"
                aria-selected={active}
                onClick={() => nav(path)}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors tap-target ${active ? 'feed-accent-border feed-accent-soft text-foreground' : 'border-[hsl(var(--app-border))] bg-[hsl(var(--app-card))] text-muted-foreground hover:text-foreground'}`}
                data-testid={`feed-switcher-${f.kind}`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
        {kind === 'marketplace' && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card))] px-3 py-1 font-mono text-[11px] text-muted-foreground">
            {coords ? `Sorting by distance from ${coords.lat.toFixed(2)}, ${coords.lng.toFixed(2)}` : 'Awaiting location…'}
          </div>
        )}
        <div className="mt-5 space-y-3" data-testid="feed-list">
          {loading && <div className="font-mono text-xs text-muted-foreground">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="rounded-xl border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card))] p-6 text-center">
              <Hash className="mx-auto h-6 w-6 feed-accent-text" />
              <p className="mt-2 text-sm">No posts yet in {config.label}.</p>
              <p className="text-xs text-muted-foreground">Be the first — every hashtag here will create a brand-new feed.</p>
              <Button className="mt-3" onClick={() => nav('/post/new')} data-testid="feed-empty-create">Create the first post</Button>
            </div>
          )}
          {items.map((p, idx) => {
            // Insert one SponsoredCard every 12 organic posts (after the slot)
            // when sponsored ads are available. Cycles through `ads` array.
            const SLOT_EVERY = 12;
            const slotPos = idx + 1;
            const sponsoredHere = ads.length > 0 && slotPos % SLOT_EVERY === 0
              ? ads[Math.floor(slotPos / SLOT_EVERY - 1) % ads.length]
              : null;
            return (
              <React.Fragment key={p.id}>
                <FeedCard post={p} onTagClick={onTagClick} sourceFeed={kind} sourceHashtag={(tags && tags[0]) || null} />
                {sponsoredHere && (
                  <SponsoredCard ad={sponsoredHere} key={`sponsored-${slotPos}-${sponsoredHere.ad_id}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
